import React from 'react';

import type { IdAdTypeDto } from '@/services/ws-consulting/src/main/resources/rest-api';
import { getAdTypes } from '@/services/ws-consulting/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import type { Option } from 'src/types';

type UseConsultingAdTypeOptionsReturn = {
  options: Option<IdAdTypeDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

type UseConsultingAdTypeOptions = () => UseConsultingAdTypeOptionsReturn;

export const useConsultingAdTypeOptions: UseConsultingAdTypeOptions = () => {
  const [searchFilter, setSearchFilter] = React.useState<string>('');

  const getAdTypesRequest = useRequest({
    apiMethod: getAdTypes,
    params: { filter: searchFilter, includeInactive: true, limit: DROP_DOWN_ENTITY_LIMIT, page: 0 },
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getAdTypesRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item.name,
        value: item.id,
        active: item.active,
      })) ?? [],
  );
  const total = useSubscribe(getAdTypesRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getAdTypesRequest.pending$);

  return React.useMemo<UseConsultingAdTypeOptionsReturn>(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
